import axios from "axios";
import ApiUrl from "../../constant/endpoint";

import {
  AUTH_LOGIN_REQUEST,
  AUTH_LOGIN_SUCCESS,
  AUTH_LOGIN_FAILURE,
  AUTH_LOGOUT_SUCCESS,
  AUTH_LOGOUT_FAILURE,
  AUTH_LOGOUT_REQUEST,
  AUTH_REGISTER_REQUEST,
  AUTH_REGISTER_FAILURE,
  AUTH_REGISTER_SUCCESS,
} from "../constants/authConstant";

// =====================================================
// BACKEND URL
// App.js sets axios.defaults.baseURL per environment (local vs production)
// before any user interaction can trigger a login; fall back to production
// only if that hasn't run for some reason.
// =====================================================
const getBackendUrl = () =>
  axios.defaults.baseURL || "https://vetumnabkend.ampnetwork.com.np";

// =====================================================
// COMMON AXIOS CONFIG
// =====================================================
const axiosConfig = {
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
};

// =====================================================
// LOGIN
// =====================================================
export const userLoginActionFn = (user) => async (dispatch) => {
  dispatch({ type: AUTH_LOGIN_REQUEST });

  try {
    // -------------------------------------------------
    // Determine login endpoint
    // -------------------------------------------------
    let loginEndpoint = ApiUrl.userLogin;

    if (user?.role === "ADMIN") {
      loginEndpoint = ApiUrl.adminLogin;
    }

    // -------------------------------------------------
    // Make sure endpoint exists
    // -------------------------------------------------
    if (!loginEndpoint) {
      throw new Error("Login endpoint is not configured.");
    }

    // -------------------------------------------------
    // ALWAYS use backend URL
    // -------------------------------------------------
    const requestUrl = `${getBackendUrl()}${loginEndpoint}`;

    console.log("=================================");
    console.log("LOGIN REQUEST");
    console.log("URL:", requestUrl);
    console.log("Role:", user?.role);
    console.log("Username:", user?.username);
    console.log("=================================");

    // -------------------------------------------------
    // Send login request
    // -------------------------------------------------
    const response = await axios.post(
      requestUrl,
      user,
      axiosConfig
    );

    const data = response?.data;

    console.log("LOGIN RESPONSE:", data);

    // -------------------------------------------------
    // Validate backend response
    // -------------------------------------------------
    if (!data || data.success !== true) {
      const failureData = data || {
        success: false,
        message: "Login failed.",
      };

      dispatch({
        type: AUTH_LOGIN_FAILURE,
        payload: failureData,
      });

      return false;
    }

    // -------------------------------------------------
    // Save user safely
    // -------------------------------------------------
    if (data.user) {
      localStorage.setItem(
        "user",
        JSON.stringify(data.user)
      );
    }

    // -------------------------------------------------
    // Save access + refresh tokens safely
    // -------------------------------------------------
    if (data.accessToken) {
      localStorage.setItem("accessToken", data.accessToken);
    }
    if (data.refreshToken) {
      localStorage.setItem("refreshToken", data.refreshToken);
    }

    // -------------------------------------------------
    // Update Redux state
    // -------------------------------------------------
    dispatch({
      type: AUTH_LOGIN_SUCCESS,
      payload: data,
    });

    return true;

  } catch (error) {
    console.error("=================================");
    console.error("LOGIN ERROR");
    console.error(error);
    console.error("=================================");

    const errorData =
      error?.response?.data || {
        success: false,
        message:
          error?.message ||
          "Unable to connect to the backend server.",
      };

    dispatch({
      type: AUTH_LOGIN_FAILURE,
      payload: errorData,
    });

    return false;
  }
};

// =====================================================
// LOGOUT
// =====================================================
export const userLogoutActionFn = () => async (dispatch) => {
  dispatch({ type: AUTH_LOGOUT_REQUEST });

  try {
    const logoutEndpoint = ApiUrl.adminLogout;

    if (!logoutEndpoint) {
      throw new Error("Logout endpoint is not configured.");
    }

    const logoutUrl = `${getBackendUrl()}${logoutEndpoint}`;

    console.log("LOGOUT URL:", logoutUrl);

    const response = await axios.get(
      logoutUrl,
      {
        withCredentials: true,
      }
    );

    const data = response?.data;

    console.log("LOGOUT RESPONSE:", data);

    // -------------------------------------------------
    // Clear authentication data regardless of backend
    // success response when logout request succeeds.
    // -------------------------------------------------
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    if (data?.success === true) {
      dispatch({
        type: AUTH_LOGOUT_SUCCESS,
        payload: null,
      });

      return true;
    }

    dispatch({
      type: AUTH_LOGOUT_FAILURE,
      payload: data || {
        success: false,
        message: "Logout failed.",
      },
    });

    return false;

  } catch (error) {
    console.error("LOGOUT ERROR:", error);

    // Clear local authentication even if the server
    // request fails, so the user is not left in a
    // partially authenticated state.
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    const errorData =
      error?.response?.data || {
        success: false,
        message:
          error?.message ||
          "Unable to connect to the backend server.",
      };

    dispatch({
      type: AUTH_LOGOUT_FAILURE,
      payload: errorData,
    });

    return false;
  }
};

// =====================================================
// REGISTER USER
// =====================================================
export const userRegisterActionFn =
  (customer) => async (dispatch) => {
    dispatch({ type: AUTH_REGISTER_REQUEST });

    try {
      console.log("REGISTRATION DATA:", customer);

      const registerEndpoint = ApiUrl.userRegister;

      if (!registerEndpoint) {
        throw new Error(
          "Registration endpoint is not configured."
        );
      }

      const registerUrl =
        `${getBackendUrl()}${registerEndpoint}`;

      console.log(
        "REGISTRATION URL:",
        registerUrl
      );

      // customer is a FormData (file uploads) — must NOT send the shared
      // axiosConfig here, since it hardcodes Content-Type: application/json.
      // That overrides the multipart boundary the browser would otherwise
      // set automatically, so the server can no longer correctly parse the
      // file fields (idFront in particular came through as missing/empty
      // even though the browser had attached a real file).
      const response = await axios.post(registerUrl, customer, {
        withCredentials: true,
      });

      const data = response?.data;

      console.log(
        "REGISTRATION RESPONSE:",
        data
      );

      // -------------------------------------------------
      // Registration successful
      // -------------------------------------------------
      if (data?.success === true) {
        dispatch({
          type: AUTH_REGISTER_SUCCESS,
          payload: data,
        });

        return { success: true };
      }

      // -------------------------------------------------
      // Registration failed
      // -------------------------------------------------
      const failurePayload = data || {
        success: false,
        message: "Registration failed.",
      };

      dispatch({
        type: AUTH_REGISTER_FAILURE,
        payload: failurePayload,
      });

      return {
        success: false,
        message: failurePayload.message || "Registration failed.",
      };

    } catch (error) {
      console.error(
        "REGISTRATION ERROR:",
        error
      );

      const errorData =
        error?.response?.data || {
          success: false,
          message:
            error?.message ||
            "Unable to connect to the backend server.",
        };

      dispatch({
        type: AUTH_REGISTER_FAILURE,
        payload: errorData,
      });

      return {
        success: false,
        message: errorData.message || "Unable to connect to the backend server.",
      };
    }
  };
